/* eslint-disable max-classes-per-file */
// eslint-disable-next-line no-restricted-syntax
import * as sessionReplay from '@amplitude/session-replay-browser';

import type { EventTrackingDecorator, TrackingEventProperties } from '@change-corgi/core/eventTracker';
import type { FCM_AMPLITUDE_SESSION_REPLAY_CONFIG, FcmConfigValueType } from '@change-corgi/fcm/configs';

type Config = FcmConfigValueType<typeof FCM_AMPLITUDE_SESSION_REPLAY_CONFIG>;

abstract class AmplitudeReplay implements EventTrackingDecorator {
	private enabled = false;
	private currentRoute: string | undefined;
	private config: Config;
	protected loggingEnabled = false;
	private running = false;

	constructor(options: { config: Config }) {
		this.config = options.config;
	}

	setEnabled(enabled: boolean): this {
		this.enabled = enabled;
		this.onChange();
		return this;
	}

	setCurrentRoute(route: string | undefined): this {
		this.currentRoute = route;
		this.onChange();
		return this;
	}

	setLoggingEnabled(loggingEnabled: boolean): this {
		this.loggingEnabled = loggingEnabled;
		return this;
	}

	protected log(message: string, ...args: unknown[]): void {
		if (this.loggingEnabled) {
			// eslint-disable-next-line no-console
			console.log(message, ...args);
		}
	}

	protected abstract start(): void;

	protected abstract stop(): void;

	protected isEnabled(): boolean {
		if (!this.enabled) {
			return false;
		}
		if (!this.currentRoute) {
			return false;
		}
		if (!this.config.enabled) {
			return false;
		}
		return !!this.config.routes[this.currentRoute];
	}

	private onChange(): void {
		if (this.isEnabled()) {
			if (!this.running) {
				this.start();
				this.running = true;
			}
			return;
		}
		if (this.running) {
			this.stop();
			this.running = false;
		}
	}

	// eslint-disable-next-line class-methods-use-this
	getEventTrackingData(): Readonly<TrackingEventProperties> {
		return {};
	}
}

class FakeAmplitudeReplay extends AmplitudeReplay {
	protected start(): void {
		this.log('[FAKE] Amplitude replay starting');
	}

	protected stop(): void {
		this.log('[FAKE] Amplitude replay stopping');
	}
}

type RealAmplitudeReplayOptions = {
	config: Config;
	apiKey: string;
	deviceId: string;
	sessionId: number;
	htmlPiiSelectors?: {
		mask: string[];
		unmask: string[];
	};
};

class RealAmplitudeReplay extends AmplitudeReplay {
	private options: RealAmplitudeReplayOptions;

	constructor(options: RealAmplitudeReplayOptions) {
		super(options);
		this.options = options;
	}

	protected start(): void {
		this.log('Amplitude replay starting');
		void (async () => {
			try {
				await sessionReplay.init(this.options.apiKey, {
					deviceId: this.options.deviceId,
					sessionId: this.options.sessionId,
					sampleRate: this.options.config.sampleRate,
					privacyConfig: {
						defaultMaskLevel: 'conservative',
						maskSelector: this.options.htmlPiiSelectors?.mask,
						unmaskSelector: this.options.htmlPiiSelectors?.unmask,
					},
				}).promise;
			} catch (error) {
				this.log('Amplitude replay failed to start', error);
			}
		})();
	}

	protected stop(): void {
		this.log('Amplitude replay stopping');
		sessionReplay.shutdown();
	}

	getEventTrackingData() {
		if (!this.isEnabled()) {
			return {};
		}
		// https://amplitude.com/docs/session-replay/session-replay-standalone-sdk#add-session-replay-id-to-your-events
		return sessionReplay.getSessionReplayProperties();
	}
}

export type { AmplitudeReplay, Config as AmplitudeReplayConfig };

export function createFakeAmplitudeReplay(options: { config: Config }): AmplitudeReplay {
	return new FakeAmplitudeReplay(options);
}

export function createAmplitudeReplay(options: RealAmplitudeReplayOptions): AmplitudeReplay {
	return new RealAmplitudeReplay(options);
}
